import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  BINARY_FONT,
  BINARY_SYSTEM,
  COLOR_DARK,
  COLOR_DIVISOR,
  FONT,
  REGEX_VALID_BINARY,
  REGEX_VALID_DECIMAL,
  REGEX_VALID_ROMAN,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, convertToDecimal } from "../helpers";
import { InputBoxRemainder } from "../strings";

/*
This component implements a input box into which the divisor,
and optionally the remainder can be entered.
If the input is wrong, it is reset and handleIncorrect is called.
If it is correct, handleCorrect is called.
*/
const InputBox = forwardRef((
  {handleCorrect,
  handleIncorrect,
  handleNoInput,
  dividend,
  divisor,
  remainder,
  whichSystem,
  showMessage,
  disableInput,},
  ref
) => {
  const [inputQuotient, setInputQuotient] = useState("");
  const [inputRemainder, setInputRemainder] = useState("");

  // These are the same ones as above, just converted to the altenate numeral system. They are updated and checked everytime the decimal ones are.
  const [inputQuotientAlternate, setInputQuotientAlternate] = useState("");
  const [inputRemainderAlternate, setInputRemainderAlternate] = useState("");

  // These Booleans store correctness of inputs, solely for the purpose of deleting wrong inputs.
  const [isQuotientCorrect, setIsQuotientCorrect] = useState(true);
  const [isRemainderCorrect, setIsRemainderCorrect] = useState(true);

  const [isQuotientAlternateCorrect, setIsQuotientAlternateCorrect] = useState(true);
  const [isRemainderAlternateCorrect, setIsRemainderAlternateCorrect] = useState(true);

   // Whether or not all input fields were filled.
  const [allFilled, setAllFilled] = useState(true);


  const handleQuotientChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputQuotient(e.target.value);
  };

  const handleQuotientAlternateChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputQuotientAlternate(e.target.value);
   };

  const handleRemainderChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputRemainder(e.target.value);
  };

  const handleRemainderAlternateChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputRemainderAlternate(e.target.value);
  };

  /**
   * Handles deleting of input fields after closing checkAnswer popup.
   */
  const manageInputsAfterCheck = () => {
    if (allFilled) { // Only delete inputs if all fields are filled.
      if (isQuotientCorrect && isRemainderCorrect && isQuotientAlternateCorrect && isRemainderAlternateCorrect) { // Everything correct.
        setInputQuotient("");
        setInputRemainder("");
        setInputQuotientAlternate("");
        setInputRemainderAlternate("");
      }
      else { // Some inputs wrong.
        if (!isQuotientCorrect) setInputQuotient("");
        if (!isRemainderCorrect) setInputRemainder("");
        if (!isQuotientAlternateCorrect) setInputQuotientAlternate("");
        if (!isRemainderAlternateCorrect) setInputRemainderAlternate("");
      }
    }
    setIsQuotientCorrect(true); // Important to set all variables to true, to make sure everything is properly initialized for the next exercise.
    setIsRemainderCorrect(true);
    setIsQuotientAlternateCorrect(true);
    setIsRemainderAlternateCorrect(true);
    setAllFilled(true);
  };

  // Only executed when showMessage is set to false, i.e. when message popup is closed after being displayed.
  useEffect(() => {
    if (!showMessage) {
      manageInputsAfterCheck();
    }
  }, [showMessage]);

  // Need to differentiate between remainder and no remainder because Roman numbers don't have a sign for the number 0.
  const checkInput = () => {
    if (remainder === 0) { // No remainder.
      if (inputQuotient === "" || inputQuotientAlternate === "") { // Not all fields filled.
        handleNoInput();
        setAllFilled(false);
      }
      else if ( // Input was correct.
      inputQuotient === (dividend / divisor).toString() && parseInt(convertToDecimal(whichSystem,inputQuotientAlternate)) === dividend / divisor) {
        handleCorrect();
        setIsQuotientCorrect(true);
        setIsRemainderCorrect(true);
        setIsQuotientAlternateCorrect(true);
        setIsRemainderAlternateCorrect(true);
      }
      else { // Some input was wrong. Mark wrong inputs.
        handleIncorrect();
        if (inputQuotient !== (dividend / divisor).toString()) {
          setIsQuotientCorrect(false);
        }
        if (parseInt(convertToDecimal(whichSystem,inputQuotientAlternate)) !== dividend / divisor) {
          setIsQuotientAlternateCorrect(false);
        }
        if (whichSystem === BINARY_SYSTEM && inputQuotientAlternate.startsWith("0")) { // If binary, we don't allow leading 0s.
          handleIncorrect(true); // This handleIncorrect call with argument "true" overrides the above call without argument.
          setIsQuotientAlternateCorrect(false);
        }
      }
    } 
    else { // Some remainder.
      if (inputQuotient === "" || inputQuotientAlternate === "" || inputRemainder === "" || inputRemainderAlternate === "") { // Not all fields filled.
        handleNoInput();
        setAllFilled(false);
      }
      else if ( // Input was correct.
      inputQuotient === ((dividend - remainder) / divisor).toString() && inputRemainder === remainder.toString() && 
      parseInt(convertToDecimal(whichSystem,inputQuotientAlternate)) === (dividend - remainder) / divisor && 
      parseInt(convertToDecimal(whichSystem, inputRemainderAlternate)) === remainder) {
        handleCorrect();
        setIsQuotientCorrect(true);
        setIsRemainderCorrect(true);
        setIsQuotientAlternateCorrect(true);
        setIsRemainderAlternateCorrect(true);
      } 
      else { // Some input was wrong. Mark wrong inputs.
        handleIncorrect();
        if (inputQuotient !== ((dividend - remainder) / divisor).toString()) {
          setIsQuotientCorrect(false);
        }
        if (inputRemainder !== remainder.toString()) {
          setIsRemainderCorrect(false);
        }
        if (parseInt(convertToDecimal(whichSystem,inputQuotientAlternate)) !== (dividend - remainder) / divisor) {
          setIsQuotientAlternateCorrect(false);
        }
        if (parseInt(convertToDecimal(whichSystem, inputRemainderAlternate)) !== remainder) {
          setIsRemainderAlternateCorrect(false);
        }
        if (whichSystem === BINARY_SYSTEM && inputQuotientAlternate.startsWith("0")) { // If binary, we don't allow leading 0s.
          handleIncorrect(true); // This handleIncorrect call with argument "true" overrides the above call without argument.
          setIsQuotientAlternateCorrect(false);
        }
        if (whichSystem === BINARY_SYSTEM && inputRemainderAlternate.startsWith("0")) { // If binary, we don't allow leading 0s.
          handleIncorrect(true); // This handleIncorrect call with argument "true" overrides the above call without argument.
          setIsRemainderAlternateCorrect(false);
        }
      }
    }
  };

  useImperativeHandle(ref, () => ({
    checkInput,
  }));

  const quotientLength = convertToAlternate(whichSystem,parseInt(dividend - remainder / divisor)).length; // Number of chars in solution. Used to scale width of input field.

  const inputStyle = {
    fontFamily: FONT,
    fontSize: "2.5dvh",
  };

  const inputStyleAlternate = {
    fontFamily: (whichSystem === ROMAN_SYSTEM) ? ROMAN_FONT : BINARY_FONT, // Works because this input field is only used for Roman and binary, not for Maya.
    fontSize: "2.5dvh",
  };

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateRows: "1fr 1fr",
          gridTemplateColumns: (remainder > 0) ? "repeat(6, max-content)" : "repeat(5, max-content)", // Widths of the columns. If there is a remainder, add an additional column.
          alignItems: (whichSystem === ROMAN_SYSTEM) ? "none" : "center", // Due to font differences, Roman font looks better without this property, and binary font looks better with it.
          justifyContent: "center",
          rowGap: "2dvh",
          fontFamily: FONT,
          fontSize: "2.5dvh",
          textShadow:"0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
          backgroundColor: COLOR_DARK,
          padding: "2dvh",
          borderRadius: "0.25em",
          boxShadow: "0.3dvh 0.3dvh 0.6dvh rgba(0, 0, 0, 0.5)",
          border: "0.2dvh solid black"
        }}
      >

        <div 
        style={{textAlign: "right", fontFamily: (whichSystem === ROMAN_SYSTEM) ? ROMAN_FONT : BINARY_FONT, marginBottom: (whichSystem === ROMAN_SYSTEM) ? "0" : "0.2dvh", marginTop: (whichSystem === ROMAN_SYSTEM ? "0.05dvh" : "0")}}> {/* Font differences. */}
          {convertToAlternate(whichSystem, dividend)}
        </div>
        <div>&nbsp;&nbsp;:&nbsp;&nbsp;</div>
        <div><span style={{ color: COLOR_DIVISOR }}>{divisor}</span></div>
        <div>&nbsp;&nbsp;=&nbsp;&nbsp;</div>
        <div><input 
          type="text"
          inputMode={(whichSystem === ROMAN_SYSTEM) ? "text" : "numeric"}
          value={inputQuotientAlternate}
          placeholder=""
          onChange={handleQuotientAlternateChange}
          className="input-field"
          pattern={(whichSystem === ROMAN_SYSTEM) ? REGEX_VALID_ROMAN : REGEX_VALID_BINARY}
          style={{...inputStyleAlternate, maxWidth: 2.1*quotientLength+"dvh", minWidth: "10dvh"}}
          disabled={disableInput}
        /></div>
        {remainder > 0 && (
        <div>
          &nbsp;&nbsp;&nbsp;{InputBoxRemainder}&nbsp;&nbsp;&nbsp;
          <input
            type="text"
            inputMode={(whichSystem === ROMAN_SYSTEM) ? "text" : "numeric"}
            value={inputRemainderAlternate}
            placeholder=""
            onChange={handleRemainderAlternateChange}
            className="input-field"
            pattern={(whichSystem === ROMAN_SYSTEM) ? REGEX_VALID_ROMAN : REGEX_VALID_BINARY}
            style={inputStyleAlternate}
            disabled={disableInput}
          />
        </div>
        )}

        <div style={{textAlign: "right"}}>{dividend}</div>
        <div>&nbsp;&nbsp;:&nbsp;&nbsp;</div>
        <div><span style={{ color: COLOR_DIVISOR }}>{divisor}</span></div>
        <div>&nbsp;&nbsp;=&nbsp;&nbsp;</div>
        <div><input 
          type="text"
          inputMode="numeric"
          value={inputQuotient}
          placeholder=""
          onChange={handleQuotientChange}
          className="input-field"
          pattern={REGEX_VALID_DECIMAL}
          style={{...inputStyle, maxWidth: 2.1*quotientLength+"dvh", minWidth: "10dvh"}}
          disabled={disableInput}
        /></div>
        {remainder > 0 && (
        <div>
          &nbsp;&nbsp;&nbsp;{InputBoxRemainder}&nbsp;&nbsp;&nbsp;
          <input
            type="text"
            inputMode="numeric"
            value={inputRemainder}
            placeholder=""
            onChange={handleRemainderChange}
            className="input-field"
            pattern={REGEX_VALID_DECIMAL}
            style={inputStyle}
            disabled={disableInput}
          />
        </div>
        )}
      </div>
  </div>
  );
});

export default InputBox;
